/**
 * Shoreline — global module.
 * Event bus, scroll-reveal controller, focus utilities, money formatting,
 * and the small custom elements every template uses.
 *
 * Duplication-safe: the bus lives on window and every define is guarded,
 * so a second module instance (e.g. versioned vs unversioned URL) is a
 * harmless no-op. theme.liquid also ships an import map pinning
 * './global.js' to the versioned URL so normally only one instance loads.
 */

/* ---------- Event bus (window-singleton) ---------- */
const bus = (window.__shorelineBus = window.__shorelineBus || { subscribers: {} });

export function subscribe(eventName, callback) {
  (bus.subscribers[eventName] = bus.subscribers[eventName] || []).push(callback);
  return () => {
    bus.subscribers[eventName] = bus.subscribers[eventName].filter((cb) => cb !== callback);
  };
}

export function publish(eventName, data) {
  (bus.subscribers[eventName] || []).forEach((cb) => cb(data));
}

window.Shoreline = window.Shoreline || { subscribe, publish };

export const PUB_SUB_EVENTS = {
  cartUpdate: 'cart:updated',
  variantChange: 'variant:changed',
};

/* ---------- Money ---------- */
export function formatMoney(cents) {
  const format = window.shorelineSettings?.moneyFormat || '${{amount}}';
  const amount = (cents / 100).toFixed(2);
  const [units, decimals] = amount.split('.');
  const withComma = units.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return format
    .replace(/\{\{\s*amount\s*\}\}/, `${withComma}.${decimals}`)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withComma)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${units}.${decimals}`);
}

/* ---------- Scroll reveal ---------- */
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
let revealObserver = null;

function initReveals() {
  const targets = document.querySelectorAll('.reveal:not(.is-revealed)');
  if (!targets.length) return;

  if (prefersReducedMotion || window.shorelineSettings?.revealOnScroll === false) {
    targets.forEach((el) => el.classList.add('is-revealed'));
    return;
  }

  if (revealObserver) revealObserver.disconnect();

  /* Fail open, always.
   *
   * `.js .reveal` starts at opacity 0 and only an IntersectionObserver
   * callback brings it back. That means anything which stops the callback
   * firing leaves the content permanently invisible rather than merely
   * un-animated — and IO callbacks are suspended in backgrounded tabs, which
   * includes headless and prerender contexts. A crawler that renders the page
   * without ever foregrounding it would see an empty hero and an empty
   * services band.
   *
   * So: if the document is hidden when we initialise, skip the animation
   * entirely and show everything. And regardless, arm a timer that reveals
   * whatever is still hidden shortly after. The animation is decoration; the
   * content is not, and decoration must never be able to hide it.
   */
  const revealAll = () => {
    document.querySelectorAll('.reveal:not(.is-revealed)').forEach((el) => el.classList.add('is-revealed'));
  };
  if (document.hidden) {
    revealAll();
    return;
  }
  window.setTimeout(revealAll, 1200);

  revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        if (!el.style.getPropertyValue('--reveal-delay')) {
          const siblings = el.parentElement
            ? [...el.parentElement.children].filter((child) => child.classList.contains('reveal'))
            : [el];
          const index = Math.max(0, siblings.indexOf(el));
          el.style.setProperty('--reveal-delay', `${Math.min(index * 75, 450)}ms`);
        }
        el.classList.add('is-revealed');
        revealObserver.unobserve(el);
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
  );

  targets.forEach((el) => revealObserver.observe(el));
}

/* ---------- Form error summary ----------
 * Shopify re-renders the page on a failed post, so the errors are already
 * in the markup. Move focus there once so keyboard and screen-reader users
 * land on the problem instead of at the top of the document.
 */
function focusFormErrors() {
  const summary = document.querySelector('.form-errors');
  if (!summary || summary.dataset.focused) return;
  summary.dataset.focused = 'true';
  summary.focus();
  summary.scrollIntoView({ block: 'center', behavior: 'auto' });
}

function initGlobal() {
  initReveals();
  focusFormErrors();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGlobal);
} else {
  initGlobal();
}
document.addEventListener('shopify:section:load', initReveals);

/* ---------- Focus utilities ---------- */
const FOCUSABLE = 'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe';

const trapHandlers = new WeakMap();

export function trapFocus(container, elementToFocus = container) {
  const focusable = Array.from(container.querySelectorAll(FOCUSABLE));
  if (!focusable.length && !elementToFocus) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  const keydown = (event) => {
    if (event.key !== 'Tab' || !focusable.length) return;
    if (document.activeElement === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }
    if ((document.activeElement === container || document.activeElement === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  removeTrapFocus();
  trapHandlers.set(document, { keydown, container });
  document.addEventListener('keydown', keydown);
  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

export function removeTrapFocus(elementToFocus = null) {
  const handler = trapHandlers.get(document);
  if (handler) {
    document.removeEventListener('keydown', handler.keydown);
    trapHandlers.delete(document);
  }
  if (elementToFocus) elementToFocus.focus();
}

window.ShorelineA11y = window.ShorelineA11y || { trapFocus, removeTrapFocus };

document.addEventListener('keyup', (event) => {
  if (event.key !== 'Escape') return;
  const openDetails = event.target.closest('details[open]');
  if (openDetails && !openDetails.hasAttribute('data-keep-open') && !openDetails.closest('.header-drawer')) {
    openDetails.removeAttribute('open');
    openDetails.querySelector('summary')?.focus();
  }
});

/* ---------- <quantity-input> ---------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    if (event.currentTarget.name === 'plus') this.input.stepUp();
    else this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }
}
if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

/* ---------- <sticky-header> ---------- */
class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.ticking = false;
    this.onScrollHandler = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.classList.toggle('is-condensed', window.scrollY > 80);
      this.ticking = false;
    });
  }
}
if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);
